// The static server sends index.html without Cache-Control, so browsers may keep serving a cached copy after a deploy and
// an admin keeps running the previous build even after a reload. The CP compares its own entry scripts with the ones in a
// freshly fetched index.html and loads the new build with a full page load.

const entryScript = /(?:^|\/)static\/js\/[^/?#]+\.js$/
const reloadMarkerKey = "sportya-cp-build-refresh"

// The build's entry scripts (/static/js/*.js with content hashes), independent of their order in the document.
export const buildSignature = doc => [...doc.querySelectorAll("script[src]")]
  .map(script => new URL(script.getAttribute("src"), "http://build.local").pathname)
  .filter(path => entryScript.test(path))
  .sort()
  .join(" ")

export const fetchLatestSignature = async () => {
  const response = await fetch((process.env.PUBLIC_URL || "") + "/index.html", { cache: "no-store", credentials: "same-origin" })
  if (!response.ok) return ""
  return buildSignature(new DOMParser().parseFromString(await response.text(), "text/html"))
}

const readMarker = storage => {
  try { return storage.getItem(reloadMarkerKey) || "" } catch (_error) { return "" }
}
const writeMarker = (storage, value) => {
  try { storage.setItem(reloadMarkerKey, value) } catch (_error) { /* Without storage the loop guard lasts for this page only. */ }
}

// running: the signature of the build on this page. latest: reads the server's current signature.
// The first check after the page loads reloads at once, before any work can be lost. A newer build found later (on a
// return to the tab or a page change) is loaded on the next page change, never in the middle of a page.
// A build is reloaded for at most once per tab, so a stale cache or proxy can never cause a reload loop.
export function createBuildRefresh({ running, latest, reload, storage = window.sessionStorage, now = Date.now, minInterval = 60000 }) {
  let pending = "", checkedAt = -Infinity, inFlight = null, started = false
  const loadNewer = () => {
    if (readMarker(storage) === pending) return false
    writeMarker(storage, pending)
    reload()
    return true
  }
  const check = () => {
    if (!running || pending || inFlight || now() - checkedAt < minInterval) return inFlight || Promise.resolve(false)
    const startup = !started
    started = true
    checkedAt = now()
    inFlight = latest()
      .then(signature => {
        if (!signature || signature === running) return false
        pending = signature
        return startup && loadNewer()
      })
      .catch(() => false)
      .finally(() => { inFlight = null })
    return inFlight
  }
  return {
    check,
    pageChanged: () => (pending ? Promise.resolve(loadNewer()) : check()),
  }
}
